import { useEffect, useMemo, useRef } from 'react';
import { putReviewPresence } from '@/lib/reviewPresenceApi';
import { ReviewPresenceLifecycle } from '@/lib/reviewPresenceLifecycle';

type UseReviewPresenceLifecycleOptions = {
    enabled: boolean;
    cycleId: number | null;
    surfaceActive: boolean;
};

export function useReviewPresenceLifecycle({
    enabled,
    cycleId,
    surfaceActive,
}: UseReviewPresenceLifecycleOptions) {
    const lifecycleRef = useRef<ReviewPresenceLifecycle | null>(null);
    const active = enabled && cycleId != null && Number.isFinite(cycleId) && cycleId > 0;

    useEffect(() => {
        if (!active || cycleId == null) {
            lifecycleRef.current?.dispose();
            lifecycleRef.current = null;
            return;
        }

        const lifecycle = new ReviewPresenceLifecycle(cycleId, putReviewPresence, true);
        lifecycleRef.current = lifecycle;

        return () => {
            lifecycle.dispose();
            if (lifecycleRef.current === lifecycle) {
                lifecycleRef.current = null;
            }
        };
    }, [active, cycleId]);

    useEffect(() => {
        lifecycleRef.current?.setSurfaceActive(surfaceActive);
    }, [surfaceActive]);

    useEffect(() => {
        if (!active) return;
        const handleVisibility = () => {
            lifecycleRef.current?.setTabVisible(document.visibilityState === 'visible');
        };
        handleVisibility();
        document.addEventListener('visibilitychange', handleVisibility);
        return () => document.removeEventListener('visibilitychange', handleVisibility);
    }, [active]);

    return useMemo(
        () => ({
            focusReviewQueue: (postingEventId: number) =>
                lifecycleRef.current?.focusReviewQueue(postingEventId),
            focusEvidenceDrawerStore: (storeId: number) =>
                lifecycleRef.current?.focusEvidenceDrawerStore(storeId),
            focusEvidenceDrawerTask: (postingEventId: number) =>
                lifecycleRef.current?.focusEvidenceDrawerTask(postingEventId),
            notifyDecisionSuccess: (options?: { willCloseSurface?: boolean }) =>
                lifecycleRef.current?.notifyDecisionSuccess(options),
        }),
        [],
    );
}
